/**
 * @fileoverview Import target-field catalog and compatibility resolution helpers.
 * @module import-editor/utils/targetFieldOptions
 *
 * Responsibilities:
 * - Define grouped target-field options shown by import mapping UIs.
 * - Resolve backend canonical suggestion fields into UI-accepted target fields.
 * - Provide human-readable labels for mapping summaries and suggestion lists.
 *
 * Dependencies:
 * - ImportDataMode from @firesquirrel/local-db.
 *
 * Architecture:
 * - Pure frontend utility shared by tenant/system import editor components.
 * - No API calls, Dexie access, or persistence side effects.
 *
 * Notes:
 * - Compatibility aliases prevent AI/rule suggestions from disappearing when backend schema names differ from legacy UI names.
 */
import type { ImportDataMode } from '@firesquirrel/local-db';

/**
 * @contract TargetFieldOption
 *
 * Purpose:
 * - Represents one selectable import target field in the mapping catalog.
 *
 * Fields:
 * - value: Persisted target field path used by import transforms.
 * - label: Human-readable label shown to users.
 * - group: UI grouping label.
 *
 * Rules:
 * - value must be a real import target path, not a custom hallucinated field.
 *
 * Sync:
 * - Static UI metadata only; saved mappings persist selected values elsewhere.
 */
export interface TargetFieldOption {
  value: string;
  label: string;
  group: string;
}

// -----------------------------------------------------------------------------
// Parcel target fields
// -----------------------------------------------------------------------------
// This catalog mirrors the shape of the InspectableEntity + ParcelDetails
// schema (see @firesquirrel/local-db InspectableEntity). Every writable scalar
// field that a county, assessor, or parcel-data provider is likely to contain
// is represented so users can map their source columns to any real target.
// Group ordering matches the order users see in the UI.
const PARCEL_FIELDS: TargetFieldOption[] = [
  // ---- Identity ----
  { value: 'parcelNumber',                          label: 'Parcel Number (APN)',        group: 'Identity' },
  { value: 'entityName',                            label: 'Entity Name',                group: 'Identity' },
  { value: 'description',                           label: 'Description',                group: 'Identity' },
  { value: 'tags',                                  label: 'Tags (comma-separated)',     group: 'Identity' },

  // ---- Address ----
  { value: 'entityLocation.address.street',         label: 'Street Address',             group: 'Address' },
  { value: 'entityLocation.address.streetNumber',   label: 'Street Number',              group: 'Address' },
  { value: 'entityLocation.address.streetName',     label: 'Street Name',                group: 'Address' },
  { value: 'entityLocation.address.city',           label: 'City',                       group: 'Address' },
  { value: 'entityLocation.address.state',          label: 'State',                      group: 'Address' },
  { value: 'entityLocation.address.zip',            label: 'Zip Code',                   group: 'Address' },
  { value: 'entityLocation.address.county',         label: 'County',                     group: 'Address' },
  { value: 'entityLocation.address.country',        label: 'Country',                    group: 'Address' },
  { value: 'entityLocation.address.fullAddress',    label: 'Full Address (single line)', group: 'Address' },
  { value: 'entityLocation.addressMetadata.additionalAddressLine', label: 'Additional Address Line', group: 'Address Metadata' },
  { value: 'entityLocation.addressMetadata.postalCode5', label: 'Five-Digit ZIP', group: 'Address Metadata' },
  { value: 'entityLocation.addressMetadata.urbanization', label: 'Address Urbanization', group: 'Address Metadata' },
  { value: 'entityLocation.addressMetadata.originalAddress', label: 'Original Physical Address', group: 'Address Metadata' },
  { value: 'entityLocation.addressMetadata.locationName', label: 'Location Name', group: 'Address Metadata' },
  { value: 'entityLocation.addressMetadata.addressSource', label: 'Address Source', group: 'Address Metadata' },

  // ---- Geometry ----
  { value: 'entityLocation.coordinates.lat',        label: 'Latitude',                   group: 'Geometry' },
  { value: 'entityLocation.coordinates.lng',        label: 'Longitude',                  group: 'Geometry' },

  // ---- Ownership facts ----
  // These fields populate parcel ownership facts and never create contacts.
  { value: 'ownership.owners.0.name',               label: 'Primary Parcel Owner',              group: 'Ownership' },
  { value: 'ownership.owners.1.name',               label: 'Additional Parcel Owner 2',        group: 'Ownership' },
  { value: 'ownership.owners.2.name',               label: 'Additional Parcel Owner 3',        group: 'Ownership' },
  { value: 'ownership.owners.3.name',               label: 'Additional Parcel Owner 4',        group: 'Ownership' },
  { value: 'ownership.owners.0.firstName',          label: 'Primary Owner First Name',          group: 'Ownership' },
  { value: 'ownership.owners.0.lastName',           label: 'Primary Owner Last Name',           group: 'Ownership' },
  { value: 'ownership.owners.0.ownerType',          label: 'Parcel Owner Type',                 group: 'Ownership' },
  { value: 'ownership.mailingAddress.line1',        label: 'Owner Mailing Street',              group: 'Ownership' },
  { value: 'ownership.mailingAddress.line2',        label: 'Owner Mailing Address Line 2',      group: 'Ownership' },
  { value: 'ownership.mailingAddress.city',         label: 'Owner Mailing City',               group: 'Ownership' },
  { value: 'ownership.mailingAddress.state',        label: 'Owner Mailing State',              group: 'Ownership' },
  { value: 'ownership.mailingAddress.postalCode',   label: 'Owner Mailing Postal Code',         group: 'Ownership' },
  { value: 'ownership.mailingAddress.countryCode',  label: 'Owner Mailing Country',             group: 'Ownership' },
  { value: 'ownership.mailingAddress.careOf',       label: 'Owner Mailing Care Of',             group: 'Ownership' },
  // Legacy ownership targets — retained for existing templates during migration.
  { value: 'parcelDetails.ownerName',               label: 'Legacy Owner Name',                 group: 'Legacy Compatibility' },
  { value: 'parcelDetails.ownerName2',              label: 'Legacy Owner Name 2',               group: 'Legacy Compatibility' },

  // ---- Legal References ----
  { value: 'parcelDetails.legalReferences.platNumber', label: 'Plat Number', group: 'Legal References' },
  { value: 'parcelDetails.legalReferences.book', label: 'Legal Book', group: 'Legal References' },
  { value: 'parcelDetails.legalReferences.page', label: 'Legal Page', group: 'Legal References' },
  { value: 'parcelDetails.legalReferences.block', label: 'Legal Block', group: 'Legal References' },
  { value: 'parcelDetails.legalReferences.lotNumber', label: 'Legal Lot Number', group: 'Legal References' },
  { value: 'parcelDetails.legalReferences.legalDescription', label: 'Legal Description', group: 'Legal References' },
  { value: 'parcelDetails.legalReferences.subdivisionName', label: 'Subdivision Name', group: 'Legal References' },
  { value: 'parcelDetails.legalReferences.neighborhoodName', label: 'Neighborhood Name', group: 'Legal References' },
  { value: 'parcelDetails.legalReferences.neighborhoodCode', label: 'Neighborhood Code', group: 'Legal References' },

  // ---- Geographic References ----
  { value: 'parcelDetails.geographicReferences.censusTract', label: 'Census Tract', group: 'Geographic References' },
  { value: 'parcelDetails.geographicReferences.censusBlock', label: 'Census Block', group: 'Geographic References' },
  { value: 'parcelDetails.geographicReferences.censusBlockGroup', label: 'Census Block Group', group: 'Geographic References' },
  { value: 'parcelDetails.geographicReferences.censusZcta', label: 'Census ZIP Area', group: 'Geographic References' },
  { value: 'parcelDetails.geographicReferences.censusCountySubdivision', label: 'Census County Subdivision', group: 'Geographic References' },
  { value: 'parcelDetails.geographicReferences.plssTownship', label: 'PLSS Township', group: 'Geographic References' },
  { value: 'parcelDetails.geographicReferences.plssSection', label: 'PLSS Section', group: 'Geographic References' },
  { value: 'parcelDetails.geographicReferences.plssRange', label: 'PLSS Range', group: 'Geographic References' },
  { value: 'parcelDetails.geographicReferences.qualifiedOpportunityZone', label: 'Qualified Opportunity Zone', group: 'Geographic References' },
  { value: 'parcelDetails.geographicReferences.qualifiedOpportunityZoneTract', label: 'Qualified Opportunity Zone Tract', group: 'Geographic References' },

  // ---- Source Metadata ----
  { value: 'sourceMetadata.sourceSystem', label: 'Source System', group: 'Source Metadata' },
  { value: 'sourceMetadata.sourceRecordId', label: 'Source Record ID', group: 'Source Metadata' },
  { value: 'sourceMetadata.sourceLocator', label: 'Source Locator', group: 'Source Metadata' },
  { value: 'sourceMetadata.sourceUrl', label: 'Source URL', group: 'Source Metadata' },
  { value: 'sourceMetadata.sourceUpdatedAt', label: 'Source Updated Date', group: 'Source Metadata' },
  { value: 'sourceMetadata.lastRefreshAt', label: 'Source Refresh Date', group: 'Source Metadata' },
  { value: 'sourceMetadata.sourceRevisionDate', label: 'Source Revision Date', group: 'Source Metadata' },
  { value: 'sourceMetadata.sourceObjectId', label: 'Source Object ID', group: 'Source Metadata' },
  { value: 'sourceMetadata.stackId', label: 'Source Stack ID', group: 'Source Metadata' },

  // ---- Sale History ----
  { value: 'parcelDetails.lastSaleDate',            label: 'Last Sale Date',             group: 'Sale History' },
  { value: 'parcelDetails.lastSalePrice',           label: 'Last Sale Price',            group: 'Sale History' },
  { value: 'parcelDetails.lastOwnershipTransferDate', label: 'Last Ownership Transfer Date', group: 'Sale History' },
  { value: 'parcelDetails.priorSaleDate',           label: 'Legacy Prior Sale Date',     group: 'Legacy Compatibility' },
  { value: 'parcelDetails.priorSalePrice',          label: 'Prior Sale Price',           group: 'Sale History' },

  // ---- Valuation & Tax ----
  { value: 'parcelDetails.taxAssessment.valueType',       label: 'Parcel Value Type',        group: 'Valuation & Tax' },
  { value: 'parcelDetails.taxAssessment.totalValue',      label: 'Parcel Total Value',       group: 'Valuation & Tax' },
  { value: 'parcelDetails.taxAssessment.landValue',       label: 'Parcel Land Value',        group: 'Valuation & Tax' },
  { value: 'parcelDetails.taxAssessment.improvementValue', label: 'Parcel Improvement Value', group: 'Valuation & Tax' },
  { value: 'parcelDetails.taxAssessment.agriculturalValue', label: 'Parcel Agricultural Value', group: 'Valuation & Tax' },
  { value: 'parcelDetails.taxAssessment.taxYear',         label: 'Tax Year',                 group: 'Valuation & Tax' },
  { value: 'parcelDetails.taxAssessment.taxAmount',       label: 'Tax Amount',               group: 'Valuation & Tax' },
  // Legacy valuation targets — retained for existing templates during migration.
  { value: 'parcelDetails.assessedValue',           label: 'Legacy Assessed Value',     group: 'Legacy Compatibility' },
  { value: 'parcelDetails.assessedLandValue',       label: 'Legacy Assessed Land Value', group: 'Legacy Compatibility' },
  { value: 'parcelDetails.assessedImprovementsValue', label: 'Legacy Assessed Improvements Value', group: 'Legacy Compatibility' },

  // ---- Land ----
  { value: 'parcelDetails.acres',                   label: 'Acres',                      group: 'Land' },
  { value: 'parcelDetails.parcelSquareFeet',       label: 'Parcel Square Feet',          group: 'Land' },
  { value: 'parcelDetails.lotSizeSqFt',             label: 'Legacy Lot Size (sq ft)',      group: 'Legacy Compatibility' },
  { value: 'parcelDetails.lotSize',                 label: 'Lot Size (text)',            group: 'Land' },
  { value: 'parcelDetails.frontage',                label: 'Frontage',                   group: 'Land' },
  { value: 'parcelDetails.topography',              label: 'Topography',                 group: 'Land' },
  { value: 'parcelDetails.floodZone',               label: 'Flood Zone',                 group: 'Land' },
  { value: 'landUse',                               label: 'Land Use',                   group: 'Land' },
  { value: 'zoning',                                label: 'Zoning',                     group: 'Land' },
  { value: 'zoning.code',                           label: 'Zoning Code',                group: 'Land' },
  { value: 'zoning.name',                           label: 'Zoning Description',          group: 'Land' },
  { value: 'propertyType',                          label: 'Property Type',              group: 'Land' },
  { value: 'propertyType.code',                     label: 'Property Type Code',         group: 'Land' },

  // ---- Parcel Structure Summary ----
  // These are parcel assessor facts, not a separate BuildingDetails entity.
  { value: 'parcelDetails.structureSummary.hasStructure', label: 'Parcel Structure Present', group: 'Parcel Structure Summary' },
  { value: 'parcelDetails.structureSummary.structureCount', label: 'Parcel Structure Count', group: 'Parcel Structure Summary' },
  { value: 'parcelDetails.structureSummary.storyCount', label: 'Parcel Story Count',      group: 'Parcel Structure Summary' },
  { value: 'parcelDetails.structureSummary.unitCount', label: 'Parcel Unit Count',         group: 'Parcel Structure Summary' },
  { value: 'parcelDetails.structureSummary.roomCount', label: 'Parcel Room Count',         group: 'Parcel Structure Summary' },
  { value: 'parcelDetails.structureSummary.bathCount', label: 'Parcel Bathroom Count',     group: 'Parcel Structure Summary' },
  { value: 'parcelDetails.structureSummary.partialBathCount', label: 'Parcel Partial Bath Count', group: 'Parcel Structure Summary' },
  { value: 'parcelDetails.structureSummary.bedroomCount', label: 'Parcel Bedroom Count',   group: 'Parcel Structure Summary' },
  { value: 'parcelDetails.structureSummary.structureStyle', label: 'Parcel Structure Style', group: 'Parcel Structure Summary' },
  { value: 'parcelDetails.structureSummary.yearBuilt', label: 'Parcel Year Built',         group: 'Parcel Structure Summary' },
  { value: 'parcelDetails.structureSummary.effectiveYearBuilt', label: 'Parcel Effective Year Built', group: 'Parcel Structure Summary' },
  { value: 'parcelDetails.structureSummary.totalBuildingSquareFeet', label: 'Parcel Total Building Area', group: 'Parcel Structure Summary' },
  { value: 'parcelDetails.structureSummary.recordedStructureSquareFeet', label: 'Parcel Recorded Structure Area', group: 'Parcel Structure Summary' },
  // Future entity-level building fields remain separate.
  { value: 'buildingDetails.buildingNumber',        label: 'Building Number',            group: 'Building Entity' },
  { value: 'buildingDetails.yearBuilt',             label: 'Building Year Built',        group: 'Building Entity' },
  { value: 'buildingDetails.squareFeet',            label: 'Building Square Feet (entity)', group: 'Building Entity' },
  { value: 'buildingDetails.floors',                label: 'Number of Floors',           group: 'Building Entity' },
  { value: 'buildingDetails.sprinklered',           label: 'Sprinklered',                group: 'Building Entity' },
  // Legacy parcel structure paths — retained for existing templates.
  { value: 'parcelDetails.yearBuilt',               label: 'Legacy Parcel Year Built',   group: 'Legacy Compatibility' },
  { value: 'parcelDetails.squareFeet',              label: 'Legacy Parcel Square Feet',  group: 'Legacy Compatibility' },
  { value: 'parcelDetails.bedroomCount',            label: 'Legacy Parcel Bedroom Count', group: 'Legacy Compatibility' },
  { value: 'parcelDetails.bathCount',               label: 'Legacy Parcel Bathroom Count', group: 'Legacy Compatibility' },
  { value: 'parcelDetails.roomCount',               label: 'Legacy Parcel Room Count',   group: 'Legacy Compatibility' },
  { value: 'buildingDetails.buildingNumber',        label: 'Building Number',            group: 'Building' },
  { value: 'buildingDetails.yearBuilt',             label: 'Building Year Built',        group: 'Building' },
  { value: 'buildingDetails.squareFeet',            label: 'Building Square Feet (entity)', group: 'Building' },
  { value: 'buildingDetails.floors',                label: 'Number of Floors',           group: 'Building' },
  { value: 'buildingDetails.sprinklered',           label: 'Sprinklered',                group: 'Building' },
  { value: 'occupancyType',                         label: 'Occupancy Type',             group: 'Building' },
  { value: 'constructionType',                      label: 'Construction Type',          group: 'Building' },

  // ---- Legal ----
  { value: 'parcelDetails.book',                    label: 'Book',                       group: 'Legal' },
  { value: 'parcelDetails.page',                    label: 'Page',                       group: 'Legal' },
  { value: 'parcelDetails.block',                   label: 'Block',                      group: 'Legal' },
  { value: 'parcelDetails.parcel',                  label: 'Parcel',                     group: 'Legal' },
  { value: 'parcelDetails.lotNumber',               label: 'Lot Number',                 group: 'Legal' },
  { value: 'parcelDetails.legalDescription',        label: 'Legal Description',          group: 'Legal' },
  { value: 'parcelDetails.subdivisionName',         label: 'Subdivision Name',           group: 'Legal' },
  { value: 'parcelDetails.deedReference',           label: 'Deed Reference',             group: 'Legal' },

  // ---- Inspection / Status ----
  { value: 'status',                                label: 'Status',                     group: 'Inspection' },
  { value: 'lastInspectionDate',                    label: 'Last Inspection Date',       group: 'Inspection' },
  { value: 'nextScheduledInspection',               label: 'Next Scheduled Inspection',  group: 'Inspection' },
  { value: 'inspectionFrequency',                   label: 'Inspection Frequency',       group: 'Inspection' },
  { value: 'inspectionAccountName',                 label: 'Inspection Account',         group: 'Inspection' },

  // ---- Contact (primary) ----
  { value: 'primaryContact.name',                   label: 'Primary Contact Name',       group: 'Contact' },
  { value: 'primaryContact.phone',                  label: 'Primary Contact Phone',      group: 'Contact' },
  { value: 'primaryContact.email',                  label: 'Primary Contact Email',      group: 'Contact' },

  // ---- Other ----
  { value: 'notes',                                 label: 'Notes',                      group: 'Other' },
];

const LOOKUP_FIELDS: TargetFieldOption[] = [
  { value: 'lookupType',        label: 'Lookup Type',        group: 'Identity' },
  { value: 'code',              label: 'Code',               group: 'Identity' },
  { value: 'name',              label: 'Name',               group: 'Identity' },
  { value: 'description',       label: 'Description',        group: 'Details' },
  { value: 'sortOrder',         label: 'Sort Order',         group: 'Details' },
  { value: 'isActive',          label: 'Is Active',          group: 'Details' },
  { value: 'parentCode',        label: 'Parent Code',        group: 'Hierarchy' },
  { value: 'notes',             label: 'Notes',              group: 'Other' },
];

const CONTACT_FIELDS: TargetFieldOption[] = [
  { value: 'name',              label: 'Full Name',          group: 'Identity' },
  { value: 'firstName',         label: 'First Name',         group: 'Identity' },
  { value: 'lastName',          label: 'Last Name',          group: 'Identity' },
  { value: 'email',             label: 'Email',              group: 'Contact' },
  { value: 'phone',             label: 'Phone',              group: 'Contact' },
  { value: 'mobilePhone',       label: 'Mobile Phone',       group: 'Contact' },
  { value: 'company',           label: 'Company',            group: 'Contact' },
  { value: 'title',             label: 'Job Title',          group: 'Contact' },
  { value: 'address',           label: 'Address',            group: 'Address' },
  { value: 'city',              label: 'City',               group: 'Address' },
  { value: 'state',             label: 'State',              group: 'Address' },
  { value: 'zip',               label: 'Zip',                group: 'Address' },
  { value: 'notes',             label: 'Notes',              group: 'Other' },
];

const GENERIC_FIELDS: TargetFieldOption[] = [];

// -----------------------------------------------------------------------------
// parcelOwnerUpdate target fields (Phase 5a)
// -----------------------------------------------------------------------------
// Update-only ownership refresh: the catalog is deliberately restricted to
// match-key candidates + ownership.* targets. Geometry, situs, and parcel
// facts are unreachable in this mode — the backend $set whitelist enforces
// the same boundary, so the UI catalog and the writer can never disagree.
const PARCEL_OWNER_UPDATE_FIELDS: TargetFieldOption[] = [
  // ---- Match key candidates ----
  { value: 'parcelNumber',                          label: 'Parcel Number (APN)',              group: 'Identity' },
  { value: 'entityName',                            label: 'Entity Name',                      group: 'Identity' },
  { value: 'sourceMetadata.sourceRecordId',         label: 'Source Record ID',                 group: 'Identity' },

  // ---- Ownership facts ----
  { value: 'ownership.owners.0.name',               label: 'Primary Parcel Owner',             group: 'Ownership' },
  { value: 'ownership.owners.1.name',               label: 'Additional Parcel Owner 2',        group: 'Ownership' },
  { value: 'ownership.owners.2.name',               label: 'Additional Parcel Owner 3',        group: 'Ownership' },
  { value: 'ownership.owners.3.name',               label: 'Additional Parcel Owner 4',        group: 'Ownership' },
  { value: 'ownership.owners.0.firstName',          label: 'Primary Owner First Name',         group: 'Ownership' },
  { value: 'ownership.owners.0.lastName',           label: 'Primary Owner Last Name',          group: 'Ownership' },
  { value: 'ownership.owners.0.ownerType',          label: 'Parcel Owner Type',                group: 'Ownership' },
  { value: 'ownership.mailingAddress.line1',        label: 'Owner Mailing Street',             group: 'Ownership' },
  { value: 'ownership.mailingAddress.line2',        label: 'Owner Mailing Address Line 2',     group: 'Ownership' },
  { value: 'ownership.mailingAddress.city',         label: 'Owner Mailing City',               group: 'Ownership' },
  { value: 'ownership.mailingAddress.state',        label: 'Owner Mailing State',              group: 'Ownership' },
  { value: 'ownership.mailingAddress.postalCode',   label: 'Owner Mailing Postal Code',        group: 'Ownership' },
  { value: 'ownership.mailingAddress.countryCode',  label: 'Owner Mailing Country',            group: 'Ownership' },
  { value: 'ownership.mailingAddress.careOf',       label: 'Owner Mailing Care Of',            group: 'Ownership' },
  // Optional per-row freshness override (e.g. a county "last transfer
  // date" column). When mapped and parseable it overrides the run-level
  // dataAsOf for that row's sourceAsOf stamp AND guard comparison.
  { value: 'ownership.sourceAsOf',                  label: 'Ownership Source Date (per-row override)', group: 'Ownership' },
];

const TARGET_FIELD_COMPAT_ALIASES: Partial<Record<ImportDataMode, Record<string, string>>> = {
  parcel: {
    name: 'entityName',
  },
};

function normalizeTargetFieldValue(value: string): string {
  return value.toLowerCase().replace(/[_\-\s\.]/g, '');
}

/**
 * @process GetTargetFieldOptions
 *
 * Steps:
 * 1. Select the static catalog for the requested import mode.
 * 2. Return an empty catalog for generic imports.
 *
 * Inputs:
 * - Import data mode.
 *
 * Outputs:
 * - Target field option list.
 *
 * Failure Handling:
 * - Unknown modes fall back to parcel options for legacy callers.
 *
 * Idempotency:
 * - REQUIRED
 */
export function getTargetFieldOptions(dataMode: ImportDataMode): TargetFieldOption[] {
  switch (dataMode) {
    case 'parcel':            return PARCEL_FIELDS;
    case 'lookup':            return LOOKUP_FIELDS;
    case 'contact':           return CONTACT_FIELDS;
    case 'generic':           return GENERIC_FIELDS;
    case 'parcelOwnerUpdate': return PARCEL_OWNER_UPDATE_FIELDS;
    default:                  return PARCEL_FIELDS;
  }
}

/**
 * @process GetTargetFieldGroups
 *
 * Steps:
 * 1. Load options for the data mode.
 * 2. Group options by their group label while preserving catalog order.
 *
 * Inputs:
 * - Import data mode.
 *
 * Outputs:
 * - Grouped target-field options.
 *
 * Failure Handling:
 * - Empty catalogs produce an empty object.
 *
 * Idempotency:
 * - REQUIRED
 */
export function getTargetFieldGroups(dataMode: ImportDataMode): Record<string, TargetFieldOption[]> {
  const options = getTargetFieldOptions(dataMode);
  return options.reduce<Record<string, TargetFieldOption[]>>((acc, opt) => {
    if (!acc[opt.group]) acc[opt.group] = [];
    acc[opt.group].push(opt);
    return acc;
  }, {});
}

/**
 * @process GetTargetFieldLabel
 *
 * Steps:
 * 1. Resolve compatibility aliases for backend suggestion fields.
 * 2. Return the matching display label or the original value.
 *
 * Inputs:
 * - Target field value and import data mode.
 *
 * Outputs:
 * - Human-readable target field label.
 *
 * Failure Handling:
 * - Unknown fields return the provided value unchanged.
 *
 * Idempotency:
 * - REQUIRED
 */
export function getTargetFieldLabel(value: string, dataMode: ImportDataMode): string {
  const options = getTargetFieldOptions(dataMode);
  const resolved = resolveTargetFieldValue(value, dataMode);
  return options.find(o => o.value === (resolved ?? value))?.label ?? value;
}

/**
 * @process ResolveTargetFieldValue
 *
 * Steps:
 * 1. Return exact catalog matches.
 * 2. Translate known backend/UI compatibility aliases.
 * 3. Return normalized catalog matches.
 *
 * Inputs:
 * - Candidate target field value and import data mode.
 *
 * Outputs:
 * - UI-accepted target field value or null.
 *
 * Failure Handling:
 * - Unknown target fields return null so custom/hallucinated suggestions are ignored.
 *
 * Idempotency:
 * - REQUIRED
 */
export function resolveTargetFieldValue(value: string, dataMode: ImportDataMode): string | null {
  const options = getTargetFieldOptions(dataMode);
  const direct = options.find(option => option.value === value);
  if (direct) return direct.value;

  const compatAlias = TARGET_FIELD_COMPAT_ALIASES[dataMode]?.[value];
  if (compatAlias && options.some(option => option.value === compatAlias)) return compatAlias;

  const normalizedValue = normalizeTargetFieldValue(value);
  const normalizedAlias = Object.entries(TARGET_FIELD_COMPAT_ALIASES[dataMode] ?? {})
    .find(([alias]) => normalizeTargetFieldValue(alias) === normalizedValue)?.[1];
  if (normalizedAlias && options.some(option => option.value === normalizedAlias)) return normalizedAlias;

  const normalized = options.find(option => normalizeTargetFieldValue(option.value) === normalizedValue);
  return normalized?.value ?? null;
}

export const ADDRESS_FIELD_OPTIONS: TargetFieldOption[] = [
  { value: 'entityLocation.address.street', label: 'Street',  group: 'Address' },
  { value: 'entityLocation.address.city',   label: 'City',    group: 'Address' },
  { value: 'entityLocation.address.state',  label: 'State',   group: 'Address' },
  { value: 'entityLocation.address.zip',    label: 'Zip',     group: 'Address' },
  { value: 'entityLocation.address.county', label: 'County',  group: 'Address' },
];
